import { gsap, WF_EASE } from './gsap'

type Cleanup = (() => void) | void
type QuickTo = ReturnType<typeof gsap.quickTo>

/**
 * Continuous MOUSE_MOVE interactions (Webflow lists a-37 / a-44..a-46 / a-40 /
 * a-32 / a-49 / a-31) plus the logo hover blur (a-35/36/50/51) and card light
 * hover (a-33/34). Pointer position is normalised to -1..1 (Webflow restingState
 * 50 = centre) and fed through gsap.quickTo for the inertial smoothing.
 */
export const setupMouseInteractions = (root: HTMLElement): Cleanup => {
    const cleanups: Array<() => void> = []

    const on = <K extends keyof HTMLElementEventMap>(
        el: HTMLElement,
        type: K,
        handler: (event: HTMLElementEventMap[K]) => void
    ): void => {
        el.addEventListener(type, handler)
        cleanups.push(() => el.removeEventListener(type, handler))
    }

    const axes = (event: MouseEvent, el: HTMLElement): { px: number; py: number } => {
        const r = el.getBoundingClientRect()
        return {
            px: ((event.clientX - r.left) / r.width) * 2 - 1,
            py: ((event.clientY - r.top) / r.height) * 2 - 1
        }
    }

    // a-37 — Hero text follows the cursor (X only).
    const heroSection = root.querySelector('.hero-section')?.closest<HTMLElement>('.section')
    if (heroSection) {
        const blur = gsap.quickTo('.hero-text-blur', 'x', { duration: 0.6, ease: 'power3' })
        const mid = gsap.quickTo('.hero-section-text-holder', 'x', { duration: 0.6, ease: 'power3' })
        const rev = gsap.quickTo('.hero-text-holder', 'x', { duration: 0.6, ease: 'power3' })

        on(heroSection, 'mousemove', (event) => {
            const { px } = axes(event, heroSection)
            blur(px * 50)
            mid(px * 10)
            rev(px * -20)
        })
        on(heroSection, 'mouseleave', () => {
            blur(0)
            mid(0)
            rev(0)
        })
    }

    // a-44 / a-45 / a-46 / a-33 / a-34 — Experience card 3D tilt + light layers + hover.
    root.querySelectorAll<HTMLElement>('.experience-card').forEach((card) => {
        gsap.set(card, { transformPerspective: 1000 })

        const text = card.querySelector<HTMLElement>('.experience-card-text-holder')
        const light = card.querySelector<HTMLElement>('.light-wrapper')
        const lightFront = card.querySelector<HTMLElement>('.light-wrapper-in-front')
        const lightOutside = card.querySelector<HTMLElement>('.light-outside')

        if (lightFront) gsap.set(lightFront, { display: 'flex' })
        if (lightOutside) gsap.set(lightOutside, { autoAlpha: 0 })

        const rotY = gsap.quickTo(card, 'rotationY', { duration: 0.5, ease: 'power3' })
        const rotX = gsap.quickTo(card, 'rotationX', { duration: 0.5, ease: 'power3' })
        const textX = text ? gsap.quickTo(text, 'x', { duration: 0.5, ease: 'power3' }) : null
        const textY = text ? gsap.quickTo(text, 'y', { duration: 0.5, ease: 'power3' }) : null
        const lightX = light ? gsap.quickTo(light, 'xPercent', { duration: 0.55, ease: 'power3' }) : null
        const lightY = light ? gsap.quickTo(light, 'yPercent', { duration: 0.55, ease: 'power3' }) : null
        const frontX = lightFront ? gsap.quickTo(lightFront, 'xPercent', { duration: 0.55, ease: 'power3' }) : null
        const frontY = lightFront ? gsap.quickTo(lightFront, 'yPercent', { duration: 0.55, ease: 'power3' }) : null

        on(card, 'mousemove', (event) => {
            const { px, py } = axes(event, card)
            rotY(px * 10)
            rotX(py * -10)
            textX?.(px * 30)
            textY?.(py * 30)
            lightX?.(px * 50)
            lightY?.(py * 50)
            frontX?.(px * 50)
            frontY?.(py * 50)
        })
        on(card, 'mouseenter', () => {
            if (lightOutside) gsap.to(lightOutside, { autoAlpha: 0.1, duration: 0.5, ease: WF_EASE })
        })
        on(card, 'mouseleave', () => {
            rotY(0)
            rotX(0)
            textX?.(0)
            textY?.(0)
            lightX?.(0)
            lightY?.(0)
            frontX?.(0)
            frontY?.(0)
            if (lightOutside) gsap.to(lightOutside, { autoAlpha: 0, duration: 0.5, ease: WF_EASE })
        })
    })

    // a-40 — Sign-up card logo looks at the cursor.
    root.querySelectorAll<HTMLElement>('.card-logo-wrapper').forEach((wrap) => {
        gsap.set(wrap, { transformPerspective: 1000 })

        const container = wrap.querySelector<HTMLElement>('.card-logo-container')
        const logo = wrap.querySelector<HTMLElement>('.card-logo')

        const rotY = container ? gsap.quickTo(container, 'rotationY', { duration: 0.6, ease: 'power3' }) : null
        const rotX = container ? gsap.quickTo(container, 'rotationX', { duration: 0.6, ease: 'power3' }) : null
        const logoX = logo ? gsap.quickTo(logo, 'x', { duration: 0.6, ease: 'power3' }) : null
        const logoY = logo ? gsap.quickTo(logo, 'y', { duration: 0.6, ease: 'power3' }) : null

        on(wrap, 'mousemove', (event) => {
            const { px, py } = axes(event, wrap)
            rotY?.(px * 40)
            rotX?.(py * -40)
            logoX?.(px * 20)
            logoY?.(py * 20)
        })
        on(wrap, 'mouseleave', () => {
            rotY?.(0)
            rotX?.(0)
            logoX?.(0)
            logoY?.(0)
        })
    })

    // a-32 / a-49 (look-at) + a-35/36/50/51 (hover blur) — Integration logos.
    root.querySelectorAll<HTMLElement>('.logo-holder').forEach((holder) => {
        gsap.set(holder, { transformPerspective: 1000 })

        const images = Array.from(holder.querySelectorAll<HTMLElement>('.logo-image'))
        const blurred = holder.querySelector<HTMLElement>('.logo-image.blured')

        if (blurred) gsap.set(blurred, { autoAlpha: 0 })

        const rotY: QuickTo | null = images.length ? gsap.quickTo(images, 'rotationY', { duration: 0.6, ease: 'power3' }) : null
        const rotX: QuickTo | null = images.length ? gsap.quickTo(images, 'rotationX', { duration: 0.6, ease: 'power3' }) : null

        on(holder, 'mousemove', (event) => {
            const { px, py } = axes(event, holder)
            rotY?.(px * 40)
            rotX?.(py * -40)
        })
        on(holder, 'mouseenter', () => {
            if (blurred) gsap.to(blurred, { autoAlpha: 0.4, duration: 1.3, ease: WF_EASE })
        })
        on(holder, 'mouseleave', () => {
            rotY?.(0)
            rotX?.(0)
            if (blurred) gsap.to(blurred, { autoAlpha: 0, duration: 1.3, ease: WF_EASE })
        })
    })

    // a-31 — Integration logo cluster drifts opposite the cursor on X.
    const integration = root.querySelector<HTMLElement>('.effortlessly-integration-section')
    const cluster = integration?.querySelector<HTMLElement>('.logo-holder-effortlessly-integration')
    if (integration && cluster) {
        const moveX = gsap.quickTo(cluster, 'x', { duration: 0.6, ease: 'power3' })
        on(integration, 'mousemove', (event) => {
            const { px } = axes(event, integration)
            moveX(px * -20)
        })
        on(integration, 'mouseleave', () => moveX(0))
    }

    return () => cleanups.forEach((fn) => fn())
}
